import config from "react-global-configuration"
import { eventChannel } from "redux-saga"
import {
  all,
  call,
  delay,
  put,
  select,
  take,
  takeLatest,
} from "redux-saga/effects"

import { request } from "lib/request"
import { Call, CallState, ResponseCall } from "types/payload"
import { GFlow, GWatcher, Message } from "types/redux"

import {
  callError,
  callHangupError,
  callHangupSuccess,
  callHasChanged,
  cancelCallError,
  cancelCallSuccess,
} from "./actions"
import { CALL_CANCEL_REQUEST, CALL_HANGUP_REQUEST, GET_CALL } from "./constants"

export const CALL_FINISHED: CallState[] = [
  CallState.ENDED,
  CallState.EXPIRED,
  CallState.CANCELLED_BY_DOCTOR,
  CallState.CANCELLED_BY_PATIENT,
]

const stepOnGoing: CallState[] = [
  CallState.INITIALIZED,
  CallState.HUNGUP,
  CallState.ACCEPTED,
  CallState.STARTED,
]

export async function getCurrentCallApi(): Promise<ResponseCall> {
  return request<ResponseCall>(config.get("call.get.current"), {
    method: "GET",
  })
}

async function hangupCallApi() {
  return request(config.get("call.hangup"), {
    method: "POST",
  })
}

async function cancelCallApi() {
  return request(config.get("call.get.current"), {
    method: "DELETE",
  })
}

function* hangupCallFlow(): GFlow<ResponseCall | Message | Call> {
  try {
    const hangupResponse: ResponseCall = yield call(hangupCallApi)
    yield all([
      put(callHangupSuccess()),
      put(callHasChanged(hangupResponse.call)),
    ])
  } catch (e) {
    yield put(callHangupError(e as string))
  }
}
let timed = false
function countdown(counter: number) {
  return eventChannel((emitter) => {
    const iv = setInterval(() => {
      emitter(counter++)
    }, config.get("timeout.call_interval"))
    return () => {
      clearInterval(iv)
    }
  })
}

function* currentCallFlow(): GFlow<any> {
  try {
    const response = yield call(getCurrentCallApi)
    // Si le timer est activé, on envoie simplement la response qui devrait arriver entre 2 interval
    // notament l'appel pour s'avoir si le docteur à raccrocher après un particiant.disconnected
    if (response.call && timed === false) yield sagaInterval(response.call)
    else if (response.call) yield put(callHasChanged(response.call))
    else {
      // If the API returns null but the store already holds a finished call (e.g.
      // CANCELLED_BY_DOCTOR), a concurrent in-flight request raced in after the
      // cancellation was already processed — ignore it to avoid wiping the state.
      const currentCall = yield select(({ client }) => client.call)
      if (!currentCall || !CALL_FINISHED.includes(currentCall.state)) {
        yield put(callHasChanged(undefined))
      }
    }
  } catch (e) {
    console.warn("[TLC Receiver Error] Cannot get TLC", e)
  }
}

export function* sagaInterval(currentCall: Call): GFlow<{ call?: Call }> {
  // On rentre dans une gestion autonome de l'appel à /current/call
  // Si l'API nous fourni un call, on va activer le timer (timed === true)
  // et chaque 10 secondes va faire un appel /current/call jusqu'à ce qu'il soit null
  timed = true
  yield put(callHasChanged(currentCall))
  const chan = yield call(countdown, 0)
  try {
    while (true) {
      yield take(chan)
      const responseCallApi = yield call(getCurrentCallApi)
      if (!responseCallApi?.call) {
        // Le dernier non-call reçu est celui qui met final_state à true
        const lastCall = yield select(({ client }) => client.call)
        if (lastCall && stepOnGoing.includes(lastCall.state)) {
          // le dernier call reçu est ongoing
          // Visiblement nous n'avons pas reçu le final_state
          yield all([
            put(
              callError(
                "Il semble qu'une demande de mise en relation soit déjà lancée sur un autre onglet.",
                0
              )
            ),
            put(callHasChanged(undefined)),
          ])
        }
        chan.close()
      } else if (CALL_FINISHED.includes(responseCallApi.call.state)) {
        // Si le call est terminé ou annulé
        yield all([put(callHasChanged(responseCallApi.call))])
        chan.close()
      } else {
        yield all([put(callHasChanged(responseCallApi.call))])
      }
    }
  } catch (error) {
    console.warn("[TLC Receiver Error] Cannot get TLC", error)
    // 401 // 429
    // yield put(callError(""))
    // Si nous ne parvenons pas à récupérer la TLC courante,
    // on relance le sagaInterval par l'intermediaire de currentCallFlow
    timed = false
    yield delay(config.get("timeout.call_interval"))
    return yield currentCallFlow()
  } finally {
    timed = false
  }
}

function* cancelCallFlow(): GFlow<Message & { call: Call }> {
  try {
    const response: ResponseCall = yield call(cancelCallApi)
    yield all([
      put(cancelCallSuccess({ call: response.call })),
      put(callHasChanged(response.call)),
    ])
  } catch (error) {
    console.log("cannot be canceled", error)
    yield put(cancelCallError(error as string))
  }
}

function* callWatcher(): GWatcher {
  yield takeLatest(GET_CALL, currentCallFlow)
  yield takeLatest(CALL_HANGUP_REQUEST, hangupCallFlow)
  yield takeLatest(CALL_CANCEL_REQUEST, cancelCallFlow)
}

export default callWatcher
